
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart2, TrendingUp, DollarSign, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Sample royalty data
const monthlyData = [
  { name: 'Jan', spotify: 452, apple: 328, amazon: 125, youtube: 210, other: 95 },
  { name: 'Feb', spotify: 480, apple: 312, amazon: 118, youtube: 265, other: 102 },
  { name: 'Mar', spotify: 510, apple: 350, amazon: 132, youtube: 280, other: 115 },
  { name: 'Apr', spotify: 530, apple: 375, amazon: 145, youtube: 310, other: 125 },
  { name: 'May', spotify: 605, apple: 392, amazon: 158, youtube: 345, other: 138 },
  { name: 'Jun', spotify: 640, apple: 410, amazon: 172, youtube: 370, other: 145 },
];

const quarterlyData = [
  { name: 'Q1', spotify: 1442, apple: 990, amazon: 375, youtube: 755, other: 312 },
  { name: 'Q2', spotify: 1775, apple: 1177, amazon: 475, youtube: 1025, other: 408 },
];

const RoyaltyTracking: React.FC = () => {
  return (
    <Card variant="forest" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2 text-white">
          <TrendingUp className="h-5 w-5 text-white/80" />
          Royalty Tracking
        </CardTitle>
        <CardDescription className="text-white/80">
          Monitor your royalty income across all streaming platforms
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row items-stretch gap-6">
          <div className="flex-1 space-y-4">
            <div className="flex items-center justify-between">
              <Tabs defaultValue="monthly" className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <TabsList className="bg-white/10 backdrop-blur-sm border border-white/20">
                    <TabsTrigger value="monthly" className="data-[state=active]:bg-white/20 text-white">Monthly</TabsTrigger>
                    <TabsTrigger value="quarterly" className="data-[state=active]:bg-white/20 text-white">Quarterly</TabsTrigger>
                  </TabsList>
                  
                  <Button variant="glass" size="sm" className="gap-1">
                    <Calendar className="h-4 w-4" />
                    2023
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
                
                <TabsContent value="monthly" className="mt-0">
                  <div className="h-80 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" />
                        <YAxis stroke="rgba(255,255,255,0.5)" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(0,0,0,0.8)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            color: 'white'
                          }} 
                        />
                        <Legend />
                        <Line type="monotone" dataKey="spotify" stroke="#1DB954" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="apple" stroke="#FC3C44" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="amazon" stroke="#00A8E1" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="youtube" stroke="#FF0000" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="other" stroke="#9b87f5" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </TabsContent>
                
                <TabsContent value="quarterly" className="mt-0">
                  <div className="h-80 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={quarterlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" />
                        <YAxis stroke="rgba(255,255,255,0.5)" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(0,0,0,0.8)', 
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            color: 'white'
                          }} 
                        />
                        <Legend />
                        <Line type="monotone" dataKey="spotify" stroke="#1DB954" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="apple" stroke="#FC3C44" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="amazon" stroke="#00A8E1" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="youtube" stroke="#FF0000" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                        <Line type="monotone" dataKey="other" stroke="#9b87f5" strokeWidth={2} dot={{ strokeWidth: 0, r: 4 }} activeDot={{ strokeWidth: 0, r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
          
          <div className="w-full md:w-64 flex-shrink-0 space-y-4">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20 space-y-4">
              <h3 className="text-sm font-medium text-white flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                Total Earnings
              </h3>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">Spotify</span>
                    <span className="text-xs font-medium text-white">$1,442.50</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#1DB954] rounded-full" style={{ width: '45%' }}></div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">Apple Music</span>
                    <span className="text-xs font-medium text-white">$892.30</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FC3C44] rounded-full" style={{ width: '28%' }}></div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">YouTube</span>
                    <span className="text-xs font-medium text-white">$520.15</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF0000] rounded-full" style={{ width: '16%' }}></div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">Amazon Music</span>
                    <span className="text-xs font-medium text-white">$218.75</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00A8E1] rounded-full" style={{ width: '7%' }}></div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">Others</span>
                    <span className="text-xs font-medium text-white">$124.80</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-soundcharts-purple rounded-full" style={{ width: '4%' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="pt-2 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/80">Total</span>
                  <span className="text-sm font-bold text-white">$3,198.50</span>
                </div>
              </div>
            </div>
            
            <Button variant="glass" size="sm" className="w-full">View Detailed Reports</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RoyaltyTracking;

